import Avatar from '@/components/base/Avatar';
import Button from '@/components/base/Button';
import { formatMoney } from '@/utils/format';
import { balanceAfterAdvance } from '@/utils/invoice';
import type { FundingWallet, PayeeInvoice, SinglePayee } from '@/types/portal';
import ReconcilePanel from './ReconcilePanel';

interface FlowDetailsStepProps {
  wallets: FundingWallet[];
  walletId: string;
  onWallet: (id: string) => void;
  currency: string;
  recipientName: string;
  recipientAvatar: string;
  targetLabel: string;
  onChangeRecipient: () => void;
  amount: number;
  onAmount: (value: number) => void;
  reason: string;
  onReason: (value: string) => void;
  payee: SinglePayee | null;
  invoice: PayeeInvoice | null;
  onCampaign: (id: string) => void;
  fee: number;
  totalDebit: number;
  error: string;
  covered: boolean;
  shortfall: number;
  canManage: boolean;
  ready: boolean;
  onSubmit: () => void;
}

/** "Please enter the payment details" — source wallet, recipient, amount and reason. */
export default function FlowDetailsStep({
  wallets,
  walletId,
  onWallet,
  currency,
  recipientName,
  recipientAvatar,
  targetLabel,
  onChangeRecipient,
  amount,
  onAmount,
  reason,
  onReason,
  payee,
  invoice,
  onCampaign,
  fee,
  totalDebit,
  error,
  covered,
  shortfall,
  canManage,
  ready,
  onSubmit,
}: FlowDetailsStepProps) {
  const money = currency || 'KES';
  const invoiceBalance = invoice ? balanceAfterAdvance(invoice.amount, invoice.advance) : 0;
  const currencies = Array.from(new Set(wallets.map((item) => item.currency)));

  return (
    <div className="space-y-6">
      <h3 className="font-heading text-lg font-semibold text-foreground-950">
        Please enter the payment details
      </h3>

      <div className="grid grid-cols-1 gap-x-8 gap-y-5 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label htmlFor="flow-from" className="text-xs font-label font-medium text-foreground-500">
            Send from
          </label>
          <select
            id="flow-from"
            value={walletId}
            onChange={(event) => onWallet(event.target.value)}
            className="mt-1 h-9 w-full cursor-pointer border-b border-background-300 bg-transparent text-sm text-foreground-900 focus:border-primary-500 focus:outline-none"
          >
            <option value="">Select a wallet...</option>
            {wallets.map((item) => (
              <option key={item.id} value={item.id}>
                {item.currency} float · {formatMoney(item.balance, item.currency)}
              </option>
            ))}
          </select>

          {/* Compact wallet balance strip */}
          <div className="mt-3 flex flex-wrap gap-2">
            {wallets.map((item) => {
              const isActive = item.id === walletId;
              const wouldCover = amount <= 0 || item.balance >= amount + fee;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => onWallet(item.id)}
                  aria-pressed={isActive}
                  className={[
                    'flex cursor-pointer items-center gap-2 rounded-full border px-3 py-1.5 text-xs transition-colors whitespace-nowrap',
                    isActive
                      ? 'border-primary-300 bg-primary-50 text-primary-800'
                      : 'border-background-200 bg-background-100 text-foreground-600 hover:border-background-300 hover:bg-background-200',
                  ].join(' ')}
                >
                  <span
                    className={[
                      'flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold',
                      isActive
                        ? 'bg-primary-200 text-primary-800'
                        : 'bg-background-200 text-foreground-500',
                    ].join(' ')}
                  >
                    {item.currency.slice(0, 2)}
                  </span>
                  <span className="font-medium">{item.currency}</span>
                  <span className={isActive ? 'text-primary-700' : 'text-foreground-400'}>
                    {formatMoney(item.balance, item.currency)}
                  </span>
                  {!wouldCover && amount > 0 ? (
                    <i
                      className="ri-error-warning-line text-[11px] text-primary-600"
                      title="Insufficient balance for this amount"
                    />
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <span className="text-xs font-label font-medium text-foreground-500">Send to</span>
          <button
            type="button"
            onClick={onChangeRecipient}
            className="mt-1 flex h-9 w-full cursor-pointer items-center gap-2 border-b border-background-300 text-left"
          >
            <Avatar src={recipientAvatar} alt={recipientName} size="xs" />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm text-foreground-900">{recipientName}</span>
            </span>
            <i className="ri-arrow-down-s-line text-foreground-400" />
          </button>
          <p className="mt-1.5 truncate text-xs text-foreground-500">{targetLabel}</p>
        </div>

        <div>
          <label
            htmlFor="flow-amount"
            className="text-xs font-label font-medium text-foreground-500"
          >
            Amount
          </label>
          <div className="mt-1 flex items-end gap-3">
            <select
              aria-label="Amount currency"
              value={money}
              onChange={(event) => {
                const match = wallets.find((item) => item.currency === event.target.value);
                if (match) onWallet(match.id);
              }}
              className="h-9 shrink-0 cursor-pointer border-b border-background-300 bg-transparent pr-1 text-sm text-foreground-900 focus:border-primary-500 focus:outline-none"
            >
              {(currencies.length > 0 ? currencies : [money]).map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </select>
            <input
              id="flow-amount"
              type="number"
              min={0}
              value={Number.isNaN(amount) || amount === 0 ? '' : amount}
              onChange={(event) => onAmount(Number(event.target.value))}
              placeholder="Enter an amount"
              className="h-9 flex-1 border-b border-background-300 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-500 focus:outline-none"
            />
          </div>
          {invoice ? (
            <button
              type="button"
              onClick={() => onAmount(invoiceBalance)}
              className="mt-1.5 inline-flex cursor-pointer items-center gap-1 text-xs font-label font-medium text-primary-700 transition-colors hover:text-primary-800"
            >
              <i className="ri-magic-line" />
              Use {invoice.advance ? 'balance after advance' : 'full invoice'} ·{' '}
              {formatMoney(invoiceBalance, money)}
            </button>
          ) : null}
        </div>

        <div>
          <label
            htmlFor="flow-reason"
            className="text-xs font-label font-medium text-foreground-500"
          >
            Payment reason
          </label>
          <input
            id="flow-reason"
            value={reason}
            maxLength={140}
            onChange={(event) => onReason(event.target.value)}
            placeholder="Optional"
            className="mt-1 h-9 w-full border-b border-background-300 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-500 focus:outline-none"
          />
        </div>
      </div>

      {payee ? (
        <ReconcilePanel payee={payee} invoice={invoice} currency={money} onCampaign={onCampaign} />
      ) : null}

      <div className="space-y-3 border-t border-background-100 pt-4">
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-foreground-500">
          <span>
            Transfer fee:{' '}
            <span className="font-medium text-foreground-800">
              {fee > 0 ? formatMoney(fee, money) : 'No fee'}
            </span>
          </span>
          <span>
            Total debit:{' '}
            <span className="font-medium text-foreground-950">{formatMoney(totalDebit, money)}</span>
          </span>
        </div>

        {!canManage ? (
          <p className="flex items-center gap-1.5 text-xs text-primary-700">
            <i className="ri-lock-line" />
            Your role can view payments but not send them.
          </p>
        ) : error ? (
          <p className="flex items-center gap-1.5 text-xs text-primary-700">
            <i className="ri-error-warning-line" />
            {error}
          </p>
        ) : !covered ? (
          <p className="flex items-center gap-1.5 text-xs text-primary-700">
            <i className="ri-error-warning-line" />
            Funding wallet is short by {formatMoney(shortfall, money)}.
          </p>
        ) : null}

        <Button
          icon="ri-send-plane-line"
          size="lg"
          onClick={onSubmit}
          disabled={!ready}
          className="w-full sm:w-auto sm:min-w-[200px]"
        >
          Send money
        </Button>
      </div>
    </div>
  );
}